var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import LoadingContainer from '@/shared/components/loading/LoadingContainer';
import { injectStateService } from '@/shared/services/StateService';
import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { TaskQueryKey } from '@/shared/hooks/task/queryKey';
import { showWhenPermission } from '@/shared/components/security';
import AuthService, { functionalities, privileges } from '@/shared/services/AuthService';
import { useGetWeekPlanner } from '@/shared/hooks/task/useGetWeekPlanner';
import { DragDropContext, Draggable, Droppable, } from '@hello-pangea/dnd';
import moment from 'moment';
import WeekPlannerElementComponent from '@/modules/tasks/weekplanner/partial/WeekPlannerElement';
import { useGetWeekPlannerCapacity } from '@/shared/hooks/task/useGetWeekPlannerCapacity';
import { minutesToTimeSpan } from '@/shared/utils/adminpulse';
import api from '@/shared/api';
import config from '@/config';
import handleHttpError from '@/shared/services/HandleHttpError';
import TaskDetailsModal from '@/modules/tasks/taskDetails/TaskDetailsModal';
import CreateToDoModal from '@/modules/tasks/CreateToDoModal';
import { useIntl } from 'react-intl';
import dateFormatService from '@/shared/services/DateFormatService';
import { WeekPlannerUnplannedTasksPanel } from '@/modules/tasks/weekplanner/partial/WeekPlannerUnplannedTasksPanel';
import { useGetWeekPlannerUnplannedTasks, } from '@/shared/hooks/task/useGetWeekPlannerUnplannedTasks';
import { defaultUnplannedTasksFilters, UNPLANNED_DRAG_PREFIX, UNPLANNED_TASKS_DROPPABLE_ID, } from '@/modules/tasks/weekplanner/unplannedTasksConstants';
import { listItemMsg } from '@/shared/components/FormattedListValue';
import weekPlannerMessages from './weekPlannerMessages';
import { WeekPlannerHeader } from './partial/WeekPlannerHeader';
var StorageKey = 'WeekPlannerParams';
var UnplannedTasksStorageKey = 'WeekPlannerUnplannedTasksParams';
var getUnplannedTasksStateFromStorage = function () {
    var _a;
    var stored = sessionStorage.getItem(UnplannedTasksStorageKey);
    if (!stored)
        return { isOpen: false, showFilters: false, filters: defaultUnplannedTasksFilters };
    try {
        var parsed = JSON.parse(stored);
        return {
            isOpen: !!parsed.isOpen,
            showFilters: !!parsed.showFilters,
            filters: __assign(__assign({}, defaultUnplannedTasksFilters), ((_a = parsed.filters) !== null && _a !== void 0 ? _a : {})),
        };
    }
    catch (_b) {
        return { isOpen: false, showFilters: false, filters: defaultUnplannedTasksFilters };
    }
};
var getEmptyState = function () {
    var executionDateFrom = moment
        .utc()
        .startOf('day')
        .startOf('isoWeek');
    return {
        taskTemplates: [],
        userId: AuthService.getUserId(),
        status: null,
        executionDateFrom: executionDateFrom.format('YYYY-MM-DD'),
        executionDateUntil: executionDateFrom
            .clone()
            .add(6, 'days')
            .format('YYYY-MM-DD'),
        showWeekend: false,
    };
};
var getFiltersFromStorage = function (props) {
    var emptyState = getEmptyState();
    if (!!props.filters && props.filters.executionDateFrom && props.filters.userId) {
        var filterFromRoute = __assign(__assign({}, emptyState), { userId: props.filters.userId, executionDateFrom: props.filters.executionDateFrom, executionDateUntil: moment(props.filters.executionDateFrom)
                .add(6, 'days')
                .format('YYYY-MM-DD') });
        sessionStorage.setItem(StorageKey, JSON.stringify(filterFromRoute));
        return filterFromRoute;
    }
    var storageString = sessionStorage.getItem(StorageKey);
    if (!storageString) {
        return emptyState;
    }
    try {
        var filterObject = JSON.parse(storageString);
        return __assign(__assign({}, emptyState), filterObject);
    }
    catch (error) {
        return emptyState;
    }
};
var getResourceTypeLabel = function (intl, type) {
    var key = "resourcetype_".concat(type);
    var translated = intl.formatMessage({ id: key, defaultMessage: key });
    return translated !== key ? translated : key;
};
var WeekPlannerContainer = function (props) {
    var _a, _b;
    var queryClient = useQueryClient();
    var intl = useIntl();
    var _c = useState([]), columns = _c[0], setColumns = _c[1];
    var canUpdateTask = useMemo(function () {
        return AuthService.hasPermission({
            functionality: functionalities.PlannedTask,
            privilege: privileges.Update,
        });
    }, []);
    var _d = useState((_a = getFiltersFromStorage(props)) !== null && _a !== void 0 ? _a : {}), activeFilters = _d[0], setActiveFilters = _d[1];
    useEffect(function () {
        sessionStorage.setItem(StorageKey, JSON.stringify(activeFilters));
    }, [activeFilters]);
    var showWeekend = !!activeFilters.showWeekend;
    var setShowWeekend = function (value) {
        return setActiveFilters(function (prev) { return (__assign(__assign({}, prev), { showWeekend: value })); });
    };
    var _e = useGetWeekPlanner(activeFilters), data = _e.data, isLoading = _e.isLoading, onRefetch = _e.refetch;
    var capacityData = useGetWeekPlannerCapacity({
        userId: activeFilters.userId,
        from: activeFilters.executionDateFrom,
        to: activeFilters.executionDateUntil,
    }).data;
    var buildColumn = function (items, index, date, dayCapacity) {
        var _a, _b;
        var totalTime = items.map(function (i) { return i.executionDuration; }).reduce(function (a, b) { return a + b; }, 0);
        var registrations = (dayCapacity === null || dayCapacity === void 0 ? void 0 : dayCapacity.registrations) || [];
        var expectedWorkingMinutes = (_a = dayCapacity === null || dayCapacity === void 0 ? void 0 : dayCapacity.expectedWorkingMinutes) !== null && _a !== void 0 ? _a : 0;
        var isHoliday = (_b = dayCapacity === null || dayCapacity === void 0 ? void 0 : dayCapacity.isHoliday) !== null && _b !== void 0 ? _b : false;
        var registrationMinutes = registrations.reduce(function (sum, r) { return sum + r.durationMinutes; }, 0);
        var workingMinutes = expectedWorkingMinutes - registrationMinutes;
        var availableMinutes = workingMinutes - totalTime;
        return {
            index: index,
            date: date,
            totalTime: totalTime,
            items: items,
            registrations: registrations,
            workingMinutes: workingMinutes,
            isHoliday: isHoliday,
            availableMinutes: availableMinutes,
            id: date.format('YYYY-MM-DD'),
            title: date.format('dddd'),
            subtitle: dateFormatService.formatDate(date),
        };
    };
    useEffect(function () {
        var content = [];
        var base = moment(activeFilters.executionDateFrom).startOf('isoWeek');
        var dayCount = showWeekend ? 7 : 5;
        var _loop_1 = function (i) {
            var day = base.clone().add(i, 'days');
            var nextDay = base.clone().add(i + 1, 'days');
            var items = (data || []).filter(function (x) {
                var _a;
                var positioningDate = (_a = x.executionDate) !== null && _a !== void 0 ? _a : x.deadline;
                return moment(positioningDate).isBetween(day, nextDay, 'days', '[)');
            });
            var dayStr = day.format('YYYY-MM-DD');
            var dayCapacity = (capacityData || []).find(function (c) { return moment(c.date).format('YYYY-MM-DD') === dayStr; });
            content.push(buildColumn(items, i, day, dayCapacity));
        };
        for (var i = 0; i < dayCount; i++) {
            _loop_1(i);
        }
        setColumns(content);
    }, [data, capacityData, activeFilters.executionDateFrom, showWeekend]);
    var weekendTaskCount = useMemo(function () {
        return (data || []).filter(function (x) {
            var _a;
            var positioningDate = (_a = x.executionDate) !== null && _a !== void 0 ? _a : x.deadline;
            var isoDay = moment(positioningDate).isoWeekday();
            return isoDay === 6 || isoDay === 7;
        }).length;
    }, [data]);
    var _f = useState({ task: undefined, open: false }), taskDetailModal = _f[0], setTaskDetailModal = _f[1];
    var openTaskDetailModal = function (task) { return setTaskDetailModal({ task: task, open: true }); };
    var _g = useState(false), showCreateToDoModal = _g[0], setShowCreateToDoModal = _g[1];
    var initialUnplannedTasks = useMemo(getUnplannedTasksStateFromStorage, []);
    var _h = useState(initialUnplannedTasks.isOpen), isUnplannedTasksOpen = _h[0], setUnplannedTasksOpen = _h[1];
    var _j = useState(initialUnplannedTasks.showFilters), showUnplannedTasksFilters = _j[0], setShowUnplannedTasksFilters = _j[1];
    var _k = useState(initialUnplannedTasks.filters), unplannedTasksFilters = _k[0], setUnplannedTasksFilters = _k[1];
    useEffect(function () {
        sessionStorage.setItem(UnplannedTasksStorageKey, JSON.stringify({
            isOpen: isUnplannedTasksOpen,
            showFilters: showUnplannedTasksFilters,
            filters: unplannedTasksFilters,
        }));
    }, [isUnplannedTasksOpen, showUnplannedTasksFilters, unplannedTasksFilters]);
    var effectiveUnplannedTasksFilters = useMemo(function () { return (__assign(__assign({}, unplannedTasksFilters), { userId: activeFilters.userId })); }, [unplannedTasksFilters, activeFilters.userId]);
    var _l = useGetWeekPlannerUnplannedTasks(effectiveUnplannedTasksFilters, isUnplannedTasksOpen), unplannedTaskItems = _l.items, isUnplannedTasksLoading = _l.isLoading, isUnplannedTasksFetchingNextPage = _l.isFetchingNextPage, fetchNextUnplannedTasksPage = _l.fetchNextPage, hasNextUnplannedTasksPage = _l.hasNextPage;
    var findDayCapacity = function (date) {
        var dayStr = date.format('YYYY-MM-DD');
        return (capacityData || []).find(function (c) { return moment(c.date).format('YYYY-MM-DD') === dayStr; });
    };
    var closeTaskDetailModal = function (tasks) {
        setTaskDetailModal({ task: undefined, open: false });
        if (!tasks)
            return;
        var map = {};
        for (var _i = 0, tasks_1 = tasks; _i < tasks_1.length; _i++) {
            var task = tasks_1[_i];
            map[task.id] = task;
        }
        var newColumns = columns.map(function (c) {
            return buildColumn(c.items.map(function (i) { return (i.id in map ? __assign(__assign({}, i), map[i.id]) : i); }), c.index, c.date, findDayCapacity(c.date));
        });
        setColumns(newColumns);
    };
    var refetch = function () {
        if (isLoading) {
            var queryKey = [TaskQueryKey.WeekPlanner];
            queryClient.cancelQueries(queryKey);
            queryClient.invalidateQueries({ queryKey: queryKey });
        }
        return onRefetch();
    };
    var setFilter = function (filter) {
        setActiveFilters(function (prev) {
            var newFilter = __assign(__assign({}, prev), filter);
            sessionStorage.setItem(StorageKey, JSON.stringify(newFilter));
            return newFilter;
        });
        setTimeout(function () {
            refetch();
        }, 0);
    };
    var removeFromUnplannedTasksCache = function (taskId) {
        return queryClient.setQueryData([TaskQueryKey.WeekPlannerUnplannedTasks, effectiveUnplannedTasksFilters], function (prev) {
            if (!prev)
                return prev;
            return __assign(__assign({}, prev), { pages: prev.pages.map(function (page) { return page.filter(function (t) { return t.id !== taskId; }); }) });
        });
    };
    var updateExecutionInformation = function (taskId, newExecutionDate) {
        var fromColumn = columns.find(function (c) { return c.items.some(function (i) { return i.id === taskId; }); });
        if (!fromColumn)
            return;
        var task = fromColumn.items.find(function (t) { return t.id === taskId; });
        if (!task)
            return;
        var toColumn = columns.find(function (c) { return c.id === newExecutionDate; });
        var sameColumn = fromColumn === toColumn;
        // A move to the same column is only a no-op when the task already sits there via its execution
        // date. A deadline-positioned task (no execution date yet) dropped on its own deadline day is a
        // real change (null -> that date), so let it through to fire the PUT and leave the panel.
        if (sameColumn && task.executionDate)
            return;
        task.executionDate = moment(newExecutionDate);
        if (sameColumn) {
            setColumns(function (prev) {
                return prev.map(function (c) { return buildColumn(c.items, c.index, c.date, findDayCapacity(c.date)); });
            });
        }
        else {
            var newColumns = __spreadArray([], columns, true);
            var fromItems = fromColumn.items.filter(function (i) { return i.id !== taskId; });
            newColumns[fromColumn.index] = buildColumn(fromItems, fromColumn.index, fromColumn.date, findDayCapacity(fromColumn.date));
            if (toColumn) {
                var toItems = __spreadArray(__spreadArray([], toColumn.items, true), [task], false);
                newColumns[toColumn.index] = buildColumn(toItems, toColumn.index, toColumn.date, findDayCapacity(toColumn.date));
            }
            setColumns(newColumns);
        }
        return api.base
            .put("".concat(config.appUrls.tasks, "/api/weekPlanner/execution/").concat(task.taskLevel, "/").concat(taskId), {
            executionDate: newExecutionDate,
        })
            .then(function (_a) {
            var updatedTask = _a.data;
            queryClient.setQueryData([TaskQueryKey.WeekPlanner], function (prev) {
                return (prev || []).map(function (t) { return (t.id === taskId ? __assign(__assign({}, t), updatedTask) : t); });
            });
            // The task now has an execution date, so drop it from the unplanned-tasks panel — patch
            removeFromUnplannedTasksCache(taskId);
        }, function (error) {
            handleHttpError(intl)(error);
        });
    };
    var mergeUpdatedTasks = function (updatedTasks) {
        if (!updatedTasks || updatedTasks.length === 0)
            return;
        var map = {};
        for (var _i = 0, updatedTasks_1 = updatedTasks; _i < updatedTasks_1.length; _i++) {
            var t = updatedTasks_1[_i];
            map[t.id] = t;
        }
        setColumns(function (prev) {
            return prev.map(function (c) {
                return buildColumn(c.items.map(function (i) { return (i.id in map ? __assign(__assign({}, i), map[i.id]) : i); }), c.index, c.date, findDayCapacity(c.date));
            });
        });
        queryClient.invalidateQueries({ queryKey: [TaskQueryKey.WeekPlanner] });
        queryClient.invalidateQueries({ queryKey: [TaskQueryKey.WeekPlannerUnplannedTasks] });
    };
    var onToDoCreated = function (todo) {
        setShowCreateToDoModal(false);
        if (!todo)
            return;
        var deadline = moment(todo.deadline);
        var weekStart = moment(activeFilters.executionDateFrom);
        var weekEnd = moment(activeFilters.executionDateUntil);
        if (deadline.isBetween(weekStart, weekEnd, 'day', '[]')) {
            refetch();
        }
    };
    var onUpdateExecutionDate = function (element, newDate) {
        if (!newDate)
            return;
        var formatted = moment(newDate).format('YYYY-MM-DD');
        var isInColumn = columns.some(function (c) { return c.items.some(function (i) { return i.id === element.id; }); });
        if (!isInColumn) {
            return moveFromUnplannedTasksToDay(element.id, formatted);
        }
        return updateExecutionInformation(element.id, formatted);
    };
    var moveFromUnplannedTasksToDay = function (taskId, targetDate) {
        var task = unplannedTaskItems.find(function (b) { return b.id === taskId; });
        if (!task)
            return undefined;
        removeFromUnplannedTasksCache(taskId);
        var targetColumn = columns.find(function (c) { return c.id === targetDate; });
        if (targetColumn) {
            var taskWithExecution = __assign(__assign({}, task), { executionDate: targetDate });
            // Strip any existing copy of this task from all columns first — the task may already be
            // shown in the grid on its deadline, and two Draggables with the same id would break dnd.
            var newColumns = columns.map(function (c) {
                return buildColumn(c.items.filter(function (i) { return i.id !== taskId; }), c.index, c.date, findDayCapacity(c.date));
            });
            newColumns[targetColumn.index] = buildColumn(__spreadArray(__spreadArray([], newColumns[targetColumn.index].items, true), [taskWithExecution], false), targetColumn.index, targetColumn.date, findDayCapacity(targetColumn.date));
            setColumns(newColumns);
        }
        return api.base
            .put("".concat(config.appUrls.tasks, "/api/weekPlanner/execution/").concat(task.taskLevel, "/").concat(taskId), {
            executionDate: targetDate,
        })
            .then(function () {
            queryClient.invalidateQueries({ queryKey: [TaskQueryKey.WeekPlanner] });
        }, function (error) {
            handleHttpError(intl)(error);
            queryClient.invalidateQueries({ queryKey: [TaskQueryKey.WeekPlanner] });
            queryClient.invalidateQueries({ queryKey: [TaskQueryKey.WeekPlannerUnplannedTasks] });
        });
    };
    var moveFromDayToUnplannedTasks = function (taskId, sourceColumnId) {
        var fromColumn = columns.find(function (c) { return c.id === sourceColumnId; });
        if (!fromColumn)
            return undefined;
        var task = fromColumn.items.find(function (i) { return i.id === taskId; });
        if (!task)
            return undefined;
        // ToDos use Deadline (not ExecutionDate) so they cannot be "unplanned" through this flow;
        if (task.taskLevel === 2)
            return undefined;
        var newColumns = __spreadArray([], columns, true);
        newColumns[fromColumn.index] = buildColumn(fromColumn.items.filter(function (i) { return i.id !== taskId; }), fromColumn.index, fromColumn.date, findDayCapacity(fromColumn.date));
        setColumns(newColumns);
        return api.base
            .put("".concat(config.appUrls.tasks, "/api/weekPlanner/execution/").concat(task.taskLevel, "/").concat(taskId), {
            executionDate: null,
        })
            .then(function () {
            queryClient.invalidateQueries({ queryKey: [TaskQueryKey.WeekPlanner] });
            queryClient.invalidateQueries({ queryKey: [TaskQueryKey.WeekPlannerUnplannedTasks] });
        }, function (error) {
            handleHttpError(intl)(error);
            queryClient.invalidateQueries({ queryKey: [TaskQueryKey.WeekPlanner] });
            queryClient.invalidateQueries({ queryKey: [TaskQueryKey.WeekPlannerUnplannedTasks] });
        });
    };
    var DRAGGING_BODY_CLASS = 'week-planner-dragging';
    var onDragStart = function () { return document.body.classList.add(DRAGGING_BODY_CLASS); };
    var onDragEnd = function (result) {
        document.body.classList.remove(DRAGGING_BODY_CLASS);
        if (!canUpdateTask)
            return;
        if (!result.destination)
            return;
        var target = result.destination.droppableId;
        var source = result.source.droppableId;
        var item = result.draggableId;
        if (target === UNPLANNED_TASKS_DROPPABLE_ID) {
            if (source === UNPLANNED_TASKS_DROPPABLE_ID)
                return;
            return moveFromDayToUnplannedTasks(item, source);
        }
        if (source === UNPLANNED_TASKS_DROPPABLE_ID) {
            // Panel draggable ids are prefixed to stay unique vs the grid copy — recover the real id.
            var taskId = item.replace(UNPLANNED_DRAG_PREFIX, '');
            return moveFromUnplannedTasksToDay(taskId, target);
        }
        return updateExecutionInformation(item, target);
    };
    var renderItem = function (provided, snapshot, item) { return (_jsx("div", __assign({ ref: provided.innerRef }, provided.draggableProps, provided.dragHandleProps, { className: "week-planner-box" }, { children: _jsx(WeekPlannerElementComponent, { element: item, isDragging: snapshot.isDragging, openTaskDetailModal: function (task) { return openTaskDetailModal(task); }, onTasksUpdated: mergeUpdatedTasks, onUpdateExecutionDate: onUpdateExecutionDate }) }))); };
    var renderColumnItems = function (column, dropProvided, dropSnapshot) {
        var overbooked = column.availableMinutes < 0;
        return (_jsxs("div", __assign({ ref: dropProvided.innerRef }, dropProvided.droppableProps, { children: [_jsxs("div", __assign({ className: "week-planner-header" }, { children: [_jsx("div", { children: column.title }), _jsx("div", __assign({ className: "week-planner-header-sub" }, { children: column.subtitle })), column.isHoliday && column.totalTime === 0 ? (_jsx("div", __assign({ className: "week-planner-header-capacity" }, { children: intl.formatMessage(weekPlannerMessages.Holiday) }))) : (_jsxs("div", __assign({ className: "week-planner-header-capacity" }, { children: [column.isHoliday ? (_jsx("div", { children: intl.formatMessage(weekPlannerMessages.Holiday) })) : (_jsxs("div", { children: [intl.formatMessage(weekPlannerMessages.WorkingTime), ":", ' ', minutesToTimeSpan(column.workingMinutes)] })), _jsxs("div", { children: [intl.formatMessage(weekPlannerMessages.Planned), ":", ' ', minutesToTimeSpan(column.totalTime)] }), _jsxs("div", __assign({ className: overbooked ? 'week-planner-header-overbooked' : '' }, { children: [intl.formatMessage(weekPlannerMessages.Available), ":", ' ', minutesToTimeSpan(column.availableMinutes)] }))] })))] })), _jsxs("div", __assign({ className: "week-planner-column-body" }, { children: [(column.registrations || []).map(function (reg, idx) { return (_jsxs("div", __assign({ className: "week-planner-registration-block" }, { children: [_jsx("span", __assign({ className: "week-planner-registration-type week-planner-registration-type-".concat(reg.type) }, { children: getResourceTypeLabel(intl, reg.type) })), _jsx("span", __assign({ className: "week-planner-registration-name" }, { children: reg.resourceId
                                        ? intl.formatMessage(listItemMsg('resource', reg.resourceId, reg.resourceName))
                                        : reg.resourceName })), _jsx("span", __assign({ className: "week-planner-registration-duration" }, { children: minutesToTimeSpan(reg.durationMinutes) }))] }), "reg-".concat(idx))); }), _jsx("div", { children: dropProvided.placeholder }), column.items.map(function (item, index) { return (_jsx(Draggable, __assign({ draggableId: item.id, index: index, isDragDisabled: !canUpdateTask }, { children: function (provided, snapshot) { return renderItem(provided, snapshot, item); } }), item.id)); })] }))] })));
    };
    var renderColumn = function (column) { return (_jsx("div", __assign({ className: "week-planner-column" }, { children: _jsx(Droppable, __assign({ droppableId: column.id, type: "ITEM", isDropDisabled: !canUpdateTask }, { children: function (dropProvided, dropSnapshot) {
                return renderColumnItems(column, dropProvided, dropSnapshot);
            } })) }), column.id)); };
    return (_jsxs(_Fragment, { children: [_jsx(WeekPlannerHeader, { activeFilters: activeFilters, setFilter: setFilter, defaultFilters: getEmptyState(), onCreateToDo: function () { return setShowCreateToDoModal(true); }, onTogglePlanTasks: function () { return setUnplannedTasksOpen(function (prev) { return !prev; }); }, isPlanTasksOpen: isUnplannedTasksOpen, showWeekend: showWeekend, onToggleWeekend: setShowWeekend, weekendTaskCount: weekendTaskCount }), _jsxs(LoadingContainer, __assign({ isLoading: isLoading && !data }, { children: [showCreateToDoModal && (_jsx(CreateToDoModal, { show: true, onSave: onToDoCreated, onClose: function () { return setShowCreateToDoModal(false); } })), taskDetailModal.open && (_jsx(TaskDetailsModal, { show: true, task: { id: (_b = taskDetailModal.task) === null || _b === void 0 ? void 0 : _b.id }, onSave: closeTaskDetailModal, onClose: closeTaskDetailModal })), _jsx("div", __assign({ className: "week-planner" }, { children: _jsx(DragDropContext, __assign({ onDragStart: onDragStart, onDragEnd: onDragEnd }, { children: _jsxs("div", __assign({ className: "week-planner-container" }, { children: [columns.map(function (column) { return renderColumn(column); }), _jsx(WeekPlannerUnplannedTasksPanel, { isOpen: isUnplannedTasksOpen, onClose: function () { return setUnplannedTasksOpen(false); }, filters: effectiveUnplannedTasksFilters, onFiltersChanged: setUnplannedTasksFilters, showFilters: showUnplannedTasksFilters, onToggleFilters: function () { return setShowUnplannedTasksFilters(function (prev) { return !prev; }); }, items: unplannedTaskItems, isLoading: isUnplannedTasksLoading, isFetchingNextPage: isUnplannedTasksFetchingNextPage, hasNextPage: hasNextUnplannedTasksPage, onLoadMore: function () { return fetchNextUnplannedTasksPage(); }, openTaskDetailModal: openTaskDetailModal, onTasksUpdated: mergeUpdatedTasks, onUpdateExecutionDate: onUpdateExecutionDate })] })) })) }))] }))] }));
};
export default showWhenPermission(functionalities.PlannedTask, privileges.Read)(injectStateService(WeekPlannerContainer));
